import { useState } from "react";

import { apiClient } from "../../api/client";
import { errorMessage } from "../admin/errors";
import { ConfirmationDialog } from "./ConfirmationDialog";
import type { UserSectionProps } from "./UserDetailDrawer";

type LifecycleAction = "disable" | "archive" | "reactivate";

export function UserLifecycleSection({ user, onUpdated }: UserSectionProps) {
  const [pending, setPending] = useState<LifecycleAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  async function confirmAction() {
    if (!pending) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const updated = await {
        disable: apiClient.disableUser.bind(apiClient),
        archive: apiClient.archiveUser.bind(apiClient),
        reactivate: apiClient.reactivateUser.bind(apiClient),
      }[pending](user.id);
      onUpdated(updated);
      setMessage(lifecycleMessage(pending));
      setPending(null);
    } catch (caught) {
      setPending(null);
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  const confirmation = pending ? lifecycleConfirmation(pending, user.username) : null;

  return (
    <section className="user-detail-section danger-zone" aria-labelledby="lifecycle-section-title">
      <div>
        <span className="eyebrow">生命周期</span>
        <h3 id="lifecycle-section-title">停用、归档与重新启用</h3>
      </div>
      <p>
        当前状态：<strong>{accountStateLabel(user.account_state)}</strong>
        。停用和归档都会撤销会话，但保留历史身份引用。
      </p>
      <div className="user-lifecycle-actions">
        {user.account_state === "active" && (
          <button disabled={busy} onClick={() => setPending("disable")} type="button">
            停用账户
          </button>
        )}
        {user.account_state !== "archived" && (
          <button disabled={busy} onClick={() => setPending("archive")} type="button">
            归档账户
          </button>
        )}
        {user.account_state !== "active" && (
          <button disabled={busy} onClick={() => setPending("reactivate")} type="button">
            重新启用
          </button>
        )}
      </div>
      {message && <p className="status-banner">{message}</p>}
      {error && (
        <p className="alert error" role="alert">
          {error}
        </p>
      )}
      {confirmation && (
        <ConfirmationDialog
          busy={busy}
          confirmLabel={confirmation.confirmLabel}
          description={confirmation.description}
          onCancel={() => setPending(null)}
          onConfirm={() => void confirmAction()}
          title={confirmation.title}
        />
      )}
    </section>
  );
}

function lifecycleConfirmation(action: LifecycleAction, username: string) {
  return {
    disable: {
      title: "确认停用账户",
      description: `停用 ${username} 并立即撤销全部活动会话。历史身份与授权记录会保留。`,
      confirmLabel: "确认停用",
    },
    archive: {
      title: "确认归档账户",
      description: `归档 ${username} 并立即撤销全部活动会话。该操作不会物理删除身份。`,
      confirmLabel: "确认归档",
    },
    reactivate: {
      title: "确认重新启用",
      description: `重新启用 ${username}。已过期的临时凭据不会恢复有效。`,
      confirmLabel: "确认重新启用",
    },
  }[action];
}

function lifecycleMessage(action: LifecycleAction): string {
  return {
    disable: "账户已停用，全部活动会话已撤销。",
    archive: "账户已归档，历史身份引用已保留。",
    reactivate: "账户已重新启用；原临时凭据有效期没有延长。",
  }[action];
}

function accountStateLabel(state: UserSectionProps["user"]["account_state"]): string {
  return { active: "已启用", disabled: "已停用", archived: "已归档" }[state];
}
